import { Component, ChangeDetectionStrategy } from '@angular/core';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { standardCommand } from '../../../core/model/command';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { BrfTransferListItem } from '../../model/Transfer/brftransfer-list-item';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CompanyService } from '../../service/data/company.service';
import { CooperativeHousingTransferService } from '../../service/data/cooperative-housing-transfer.service';
import { transferCommand } from '../../command/transfer.command';
import { CooperativeHousingTransferCommandHandlerResolver } from '../../service/command/cooperative-housing-transfer-command-handler-resolver.service';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { UpdateTransferCommandHandler } from '../../../accounting/service/command/update-transfer-command-handler.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { CooperativeHousingTransferCommandFactory } from '../../service/command/factory/cooperative-housing-transfer-command-factory.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { TransferType, transferTypeToString } from '../../model/const/transfer-type';
import { ColumnDefinition } from '../../../core/model/column-definition';
import ListViewColumn from '../../../core/model/list-view-column';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-brf-transfer-manager',
    templateUrl: './cooperative-housing-transfer-manager.component.html',
    styleUrls: ['./cooperative-housing-transfer-manager.component.scss'],
    providers: [
        NavigationService,
        UpdateTransferCommandHandler,
        { provide: DataService, useExisting: CooperativeHousingTransferService },
        { provide: CommandHandlerResolver, useExisting: CooperativeHousingTransferCommandHandlerResolver },
        { provide: CommandFactoryService, useClass: CooperativeHousingTransferCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CooperativeHousingTransferManagerComponent extends MultiselectObjectManagerComponentBase<BrfTransferListItem> {
  excelColumnDefinition: ColumnDefinition<BrfTransferListItem>[] = [];
  excelContext = 'BrfTransfer';
  excelFileName = 'Överlåtelser';
  selectedColumns: ListViewColumn<BrfTransferListItem>[] = [];
  selectedTransferIds: string[] = [];
  private pendingSelectedColumns: ListViewColumn<BrfTransferListItem>[] = [];
  private columnUpdateScheduled = false;
  private filterResultsLoaded = false;
  private filterResultSubscription?: Subscription;

  constructor(
    columnDefinitionService: ColumnDefinitionService,
    private readonly _dataService: CooperativeHousingTransferService,
    private readonly _companyService: CompanyService,
    dispatcher: CommandDispatcher,
    messageService: MessageService,
    navigationService: NavigationService,
    handlerResolver: CommandHandlerResolver
  ) {
    super(_dataService, dispatcher, handlerResolver, messageService, navigationService);

    this.allowNew = true;
    this.columnDefinition = columnDefinitionService.create('CooperativeHousingTransfer');
    this.excelColumnDefinition = [...this.columnDefinition];
    this.title = 'COOPERATIVE_HOUSING.TRANSFER_LIST_HEADER';
    this.preferencesKey = 'rent.cooperative-housing-transfer.list';
  }

  get excelReady(): boolean {
    return this.filterResultsLoaded && this.selectedColumns.length > 0;
  }

  get selectedTransferCount(): number {
    return this.selectedTransferIds.length;
  }

  get allTransfersSelected(): boolean {
    return this.selectedTransferCount > 0 && this.selectedTransferCount === this.itemsSource?.length;
  }

  get totalTransfers(): number {
    return this.itemsSource?.length ?? 0;
  }

  onColumnChange(columns: ListViewColumn<BrfTransferListItem>[]): void {
    this.pendingSelectedColumns = columns.filter(column => column.selected);
    if (this.columnUpdateScheduled) return;

    this.columnUpdateScheduled = true;
    queueMicrotask(() => {
      this.columnUpdateScheduled = false;
      this.selectedColumns = this.pendingSelectedColumns;
    });
  }

  onSelectedTransfersChange(transfers: BrfTransferListItem[]): void {
    this.selectedTransferIds = transfers.map(transfer => transfer.id);
  }

  override onFilterChange(filterState: any): void {
    if (!filterState?.selectedFields || this.filterDefinition === undefined) return;

    this.filterState = filterState;
    this.filterInternal(filterState);
    this.selectedTransferIds = [];
    this.itemsSource = undefined;
    this.filterResultsLoaded = false;
    this.filterResultSubscription?.unsubscribe();
    this.filterResultSubscription = this._dataService.getFilteredList(filterState).subscribe({
      next: items => {
        this.itemsSource = items;
        this.filterResultsLoaded = true;
      },
      error: error => {
        this.itemsSource = undefined;
        this.filterResultsLoaded = false;
        this.messageService.showError(error);
      },
    });
  }

  protected override clearAdditionalSubscriptions(): void {
    super.clearAdditionalSubscriptions();
    this.filterResultSubscription?.unsubscribe();
    this.filterResultSubscription = undefined;
  }

  ngOnInit() {
    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.defaultFields;
        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMMON.COMPANY',
            'companyId',
            definition.companyId,
            this._companyService,
            definition.selectedFields.includes('companyId')
          ),
          new FilterDefinitionItem(
            'COMMON.INCOME_YEAR',
            'incomeYear',
            definition.incomeYear,
            null,
            definition.selectedFields.includes('incomeYear')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
            'rentalId',
            definition.rentalId,
            null,
            definition.selectedFields.includes('rentalId')
          ),
          new FilterDefinitionItem(
            'COOPERATIVE_HOUSING.TRANSFER_SELLER',
            'sellerContactName',
            definition.sellerContactName,
            null,
            definition.selectedFields.includes('sellerContactName')
          ),
          new FilterDefinitionItem(
            'COOPERATIVE_HOUSING.TRANSFER_TYPE',
            'transferType',
            definition.transferType,
            [
              { caption: 'ALL_TEXT', value: null },
              { caption: transferTypeToString(TransferType.Purchase), value: TransferType.Purchase },
              { caption: transferTypeToString(TransferType.ForcedSale), value: TransferType.ForcedSale },
              { caption: transferTypeToString(TransferType.Renunciation), value: TransferType.Renunciation },
              { caption: transferTypeToString(TransferType.Gift), value: TransferType.Gift },
              { caption: transferTypeToString(TransferType.Inheritance), value: TransferType.Inheritance },
              { caption: transferTypeToString(TransferType.PartitionOfProperty), value: TransferType.PartitionOfProperty },
            ],
            definition.selectedFields.includes('transferType')
          ),
        ];
      })
    );

    this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
    this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
    this.addMultiselectMenuItem('COOPERATIVE_HOUSING.UPDATE_TRANSFER', transferCommand.updateTransfer);
  }
}
